import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Edit2, Utensils, ChevronDown, ChevronUp, Image as ImageIcon, Camera, X, LayoutGrid } from 'lucide-react';
import MobileView from './MobileView';
import AddItemDialog from './AddItemDialog';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, useUsageStore } from '../../../stores';
import { toast } from 'react-toastify';

const CURRENCIES = [

    { code: 'QAR', symbol: 'QR', name: 'Qatari Riyal' },
    { code: 'DZD', symbol: 'DZD', name: 'Algerian Dinar' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'SAR', symbol: '⃁', name: 'Saudi Riyal' },
    { code: 'AED', symbol: 'AED', name: 'UAE Dirham' },
];



const THEME_COLOR = '#c59253';

const MenuConfig = ({ state, updateState }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { usage, getLimitInfo } = useUsageStore();
    const [activeSection, setActiveSection] = useState(state.sections?.[0]?.id || null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [currency, setCurrency] = useState(state.siteInfo?.currency || 'USD');

    // Safe checks for arrays
    const sections = state.sections || [];
    const totalItems = sections.reduce((acc, section) => acc + (section.items?.length || 0), 0);

    const menuLimit = getLimitInfo("menus");
    const itemLimit = getLimitInfo("menu_items");

    const isMaxMenusReached = menuLimit?.isReached || (menuLimit?.limit !== null && (menuLimit?.current + sections.length) > menuLimit?.limit);
    const isMaxItemsReached = itemLimit?.isReached || (itemLimit?.limit !== null && (itemLimit?.current + totalItems) > itemLimit?.limit);

    const addSection = () => {
        if (isMaxMenusReached) return;
        const newSection = {
            id: Date.now().toString(),
            name: `${t('Section')} ${(state.sections?.length || 0) + 1}`,
        };
        updateState({ sections: [...(state.sections || []), newSection] });
        setActiveSection(newSection.id);
    };

    const deleteSection = (id, e) => {
        e.stopPropagation();
        updateState({
            sections: state.sections.filter((s) => s.id !== id),
        });
        if (activeSection === id) {
            setActiveSection(null);
        }
    };

    const updateSection = (id, data) => {
        updateState({
            sections: state.sections.map((s) => (s.id === id ? { ...s, ...data } : s)),
        });
        setEditingSection(null);
    };


    const addItem = (item) => {
        if (!activeSection) return;

        if (editingItem) {
            // Update existing item
            const updatedSections = sections.map(section => {
                if (section.id === activeSection) {
                    return {
                        ...section,
                        items: (section.items || []).map(i => i.id === editingItem.id ? { ...item, id: i.id } : i)
                    };
                }
                return section;
            });
            updateState({ sections: updatedSections });
            setEditingItem(null);
            return;
        }

        if (isMaxItemsReached) {
            toast.error(t('Item limit reached for your plan'));
            return;
        }

        const newItem = {
            ...item,
            id: Date.now().toString() + Math.random(),
            // sectionId: activeSection, // No longer strictly needed if nested, but good for reference
        };

        const updatedSections = sections.map(section => {
            if (section.id === activeSection) {
                return {
                    ...section,
                    items: [...(section.items || []), newItem]
                };
            }
            return section;
        });

        updateState({ sections: updatedSections });
    };

    const deleteItem = (id) => {
        const updatedSections = sections.map(section => {
            if (section.id === activeSection) {
                return {
                    ...section,
                    items: (section.items || []).filter(item => item.id !== id)
                };
            }
            return section;
        });

        updateState({ sections: updatedSections });
    };

    const handleEditItem = (item) => {
        setEditingItem(item);
        setIsDialogOpen(true);
    };

    // Find active section and its items
    const activeSectionData = sections.find(s => s.id === activeSection);
    const activeSectionItems = activeSectionData?.items || [];

    // Limits UI
    const isLimitReached = isMaxItemsReached;

    const handleSectionImage = (sectionId, e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                updateSection(sectionId, {
                    imagePreview: reader.result,
                    imageFile: file
                });
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <div className="h-100 d-flex flex-column flex-lg-row overflow-hidden bg-light bg-opacity-50">
            {/* Main Area: Section Accordion */}
            <div className="flex-grow-1 p-3 p-lg-4 overflow-auto">
                <div className="max-w-4xl mx-auto" style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <div className="d-flex align-items-center justify-content-between mb-4">
                        <div>
                            <h3 className="h5 mb-1 fw-bold">{t("Let's Build Your Menu")}</h3>
                            <p className="text-muted small mb-0">{t('Organize your menu into sections')}</p>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            {isMaxMenusReached && (
                                <span className="text-danger small fw-bold d-none d-md-inline">
                                    {t('Limit Reached')} ({menuLimit.limit})
                                </span>
                            )}
                            <button
                                onClick={addSection}
                                className="btn d-flex align-items-center gap-2 btn-sm text-white px-3 shadow-sm"
                                style={{ backgroundColor: THEME_COLOR, borderColor: THEME_COLOR, borderRadius: '10px', opacity: isMaxMenusReached ? 0.6 : 1 }}
                                disabled={isMaxMenusReached}
                                title={isMaxMenusReached ? t("Section limit reached") : t("Add Section")}
                            >
                                <Plus size={16} />
                                {t('Add Section')}
                            </button>
                        </div>
                    </div>
                    {isLimitReached && (
                        <div className="alert alert-warning border-0 rounded-4 shadow-sm d-flex align-items-center gap-2 mb-4">
                            <span className="badge bg-warning text-dark rounded-circle p-1">
                                <LayoutGrid size={12} />
                            </span>
                            <span className="small fw-medium">{t('Item limit reached')} ({itemLimit.limit} {t('items')}). {t('Content may be limited.')}</span>
                        </div>
                    )}

                    <div className="d-flex flex-column gap-3 mb-5">
                        {sections.map((section) => (
                            <div
                                key={section.id}
                                className={`card border-0 shadow-sm rounded-4 overflow-hidden transition-all ${activeSection === section.id ? 'ring-2' : ''}`}
                                style={activeSection === section.id ? { ringColor: THEME_COLOR, ringWidth: '2px' } : {}}
                            >
                                {/* Section Header */}
                                <div
                                    className="card-header bg-white border-0 p-3 d-flex align-items-center justify-content-between cursor-pointer"
                                    onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
                                >
                                    <div className="d-flex align-items-center gap-3 min-w-0">
                                        <div
                                            className="rounded-3 bg-light d-flex align-items-center justify-content-center overflow-hidden border"
                                            style={{ width: '40px', height: '40px' }}
                                        >
                                            {section.imagePreview ? (
                                                <img src={section.imagePreview} className="w-100 h-100 object-fit-cover" alt="" />
                                            ) : (
                                                <ImageIcon size={18} className="text-muted opacity-50" />
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="d-flex align-items-center gap-2">
                                                <h6 className="mb-0 fw-bold text-dark text-truncate">{section.name}</h6>
                                                {section.name_ar && <span className="text-muted small d-none d-sm-inline">• {section.name_ar}</span>}
                                            </div>
                                            <span className="text-muted x-small d-block mt-0.5">
                                                {section.items?.length || 0} {t('items')}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="d-flex align-items-center gap-1">
                                            <button
                                                className="btn btn-link btn-sm p-1 text-muted hover-text-danger transition-colors"
                                                onClick={(e) => { e.stopPropagation(); deleteSection(section.id, e); }}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                        <div className={`transition-transform duration-300 ${activeSection === section.id ? 'rotate-180' : ''}`}>
                                            <ChevronDown size={20} className="text-muted" />
                                        </div>
                                    </div>
                                </div>

                                {/* Section Content */}
                                <AnimatePresence>
                                    {activeSection === section.id && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                                        >
                                            <div className="card-body p-3 pt-0 border-top bg-light bg-opacity-25">
                                                {/* Section Config Area */}
                                                <div className="bg-white p-3 rounded-4 border-light border mt-3 mb-4 shadow-sm">
                                                    <div className="d-flex align-items-center gap-2 mb-3">
                                                        <div className="p-1 rounded bg-primary bg-opacity-10 text-primary">
                                                            <Edit2 size={14} />
                                                        </div>
                                                        <span className="small fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>{t('Section Configuration')}</span>
                                                    </div>

                                                    <div className="row g-3">
                                                        {/* Image Upload */}
                                                        <div className="col-12 col-md-auto">
                                                            <div className="position-relative mx-auto mx-md-0" style={{ width: '100px' }}>
                                                                <div
                                                                    className="rounded-4 bg-light border-dashed border-2 d-flex flex-column align-items-center justify-content-center cursor-pointer overflow-hidden transition-all hover-bg-silver"
                                                                    style={{ height: '100px', width: '100px' }}
                                                                    onClick={() => document.getElementById(`section-img-${section.id}`).click()}
                                                                >
                                                                    {section.imagePreview ? (
                                                                        <img src={section.imagePreview} className="w-100 h-100 object-fit-cover" alt="" />
                                                                    ) : (
                                                                        <>
                                                                            <Camera size={24} className="text-muted mb-1 opacity-50" />
                                                                            <span style={{ fontSize: '8px' }} className="fw-bold text-muted">{t('UPLOAD')}</span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                <input
                                                                    type="file"
                                                                    id={`section-img-${section.id}`}
                                                                    className="d-none"
                                                                    accept="image/*"
                                                                    onChange={(e) => handleSectionImage(section.id, e)}
                                                                />
                                                                {section.imagePreview && (
                                                                    <button
                                                                        className="btn btn-danger btn-sm rounded-circle p-1 position-absolute top-0 end-0 m-1 shadow-sm border-0"
                                                                        onClick={(e) => { e.stopPropagation(); updateSection(section.id, { imagePreview: null, imageFile: null }); }}
                                                                        style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                    >
                                                                        <X size={12} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Text Fields */}
                                                        <div className="col">
                                                            <div className="row g-2">
                                                                <div className="col-12 col-sm-6">
                                                                    <label className="x-small fw-bold text-muted mb-1 d-block" style={{ fontSize: '9px' }}>{t('NAME (EN)')}</label>
                                                                    <input
                                                                        className="form-control form-control-sm rounded-3 border-secondary border-opacity-25 shadow-none"
                                                                        value={section.name}
                                                                        onChange={(e) => updateSection(section.id, { name: e.target.value })}
                                                                    />
                                                                </div>
                                                                <div className="col-12 col-sm-6">
                                                                    <label className="x-small fw-bold text-muted mb-1 d-block text-end" style={{ fontSize: '9px' }}>{t('Name (AR)')}</label>
                                                                    <input
                                                                        dir="rtl"
                                                                        className="form-control form-control-sm rounded-3 border-secondary border-opacity-25 shadow-none text-end"
                                                                        value={section.name_ar || ''}
                                                                        onChange={(e) => updateSection(section.id, { name_ar: e.target.value })}
                                                                    />
                                                                </div>
                                                                <div className="col-12 col-sm-6">
                                                                    <label className="x-small fw-bold text-muted mb-1 d-block" style={{ fontSize: '9px' }}>{t('DESC (EN)')}</label>
                                                                    <input
                                                                        className="form-control form-control-sm rounded-3 border-secondary border-opacity-25 shadow-none"
                                                                        placeholder={t("Optional description")}
                                                                        value={section.description || ''}
                                                                        onChange={(e) => updateSection(section.id, { description: e.target.value })}
                                                                    />
                                                                </div>
                                                                <div className="col-12 col-sm-6">
                                                                    <label className="x-small fw-bold text-muted mb-1 d-block text-end" style={{ fontSize: '9px' }}>{t('Description (AR)')}</label>
                                                                    <input
                                                                        dir="rtl"
                                                                        className="form-control form-control-sm rounded-3 border-secondary border-opacity-25 shadow-none text-end"
                                                                        placeholder={t("وصف اختياري")}
                                                                        value={section.description_ar || ''}
                                                                        onChange={(e) => updateSection(section.id, { description_ar: e.target.value })}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Items Header */}
                                                <div className="d-flex align-items-center justify-content-between mb-3 px-1">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div className="p-1 rounded bg-secondary bg-opacity-10 text-secondary">
                                                            <Utensils size={14} />
                                                        </div>
                                                        <span className="small fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                                            {t('Menu Items')} ({section.items?.length || 0})
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Item List */}
                                                <div className="d-flex flex-column gap-2 mb-2">
                                                    {(section.items || []).map((item) => (
                                                        <div key={item.id} className="card border-light bg-white rounded-4 overflow-hidden shadow-none transition-all hover-border-silver hover-shadow-sm group-item">
                                                            <div className="p-2 px-3">
                                                                <div className="d-flex gap-3 align-items-center">
                                                                    <div className="bg-light rounded-3 overflow-hidden flex-shrink-0 border shadow-xs" style={{ width: '50px', height: '50px' }}>
                                                                        {item.imagePreview ? (
                                                                            <img src={item.imagePreview} alt={item.name} className="w-100 h-100 object-fit-cover" />
                                                                        ) : (
                                                                            <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-muted opacity-30 bg-white">
                                                                                <Utensils size={12} />
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    <div className="flex-grow-1 min-w-0">
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <h6 className="mb-0 text-truncate fw-bold text-dark" style={{ fontSize: '0.85rem' }}>{item.name}</h6>
                                                                            {item.is_promo && <span className="bg-danger rounded-circle" style={{ width: '6px', height: '6px' }} title="On Promo"></span>}
                                                                        </div>
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <span className="fw-bold text-primary" style={{ fontSize: '0.8rem' }}>
                                                                                {item.is_promo && item.promo_price
                                                                                    ? `${currency}${item.promo_price}`
                                                                                    : (item.price ? `${currency}${item.price}` : t('Free'))}
                                                                            </span>
                                                                            {item.tags?.length > 0 && (
                                                                                <span className="text-muted x-small d-none d-sm-inline opacity-50" style={{ fontSize: '9px' }}>
                                                                                    • {item.tags[0]} {item.tags.length > 1 ? `+${item.tags.length - 1}` : ''}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {item.options?.length > 0 && (
                                                                            <div className="d-flex flex-wrap gap-1 mt-1">
                                                                                {item.options.map((opt, i) => (
                                                                                    <span key={i} className="badge bg-light text-muted border-0 fw-normal" style={{ fontSize: '8px', padding: '2px 6px' }}>
                                                                                        {opt.name}: {opt.choices?.length || 0}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    <div className="d-flex gap-1">
                                                                        <button
                                                                            onClick={() => handleEditItem(item)}
                                                                            className="btn btn-sm p-1 rounded-circle hover-bg-light text-muted"
                                                                        >
                                                                            <Edit2 size={13} />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => deleteItem(item.id)}
                                                                            className="btn btn-sm p-1 rounded-circle hover-bg-light text-danger"
                                                                        >
                                                                            <Trash2 size={13} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}

                                                    {/* Add Item Card Button */}
                                                    <motion.div
                                                        whileHover={{ scale: 1.01 }}
                                                        whileTap={{ scale: 0.99 }}
                                                        onClick={() => {
                                                            setActiveSection(section.id);
                                                            setEditingItem(null);
                                                            setIsDialogOpen(true);
                                                        }}
                                                        className="card border-2 border-dashed bg-white rounded-4 overflow-hidden shadow-sm transition-all cursor-pointer add-item-card"
                                                        style={{
                                                            borderColor: THEME_COLOR,
                                                            opacity: isLimitReached ? 0.5 : 1,
                                                            pointerEvents: isLimitReached ? 'none' : 'auto'
                                                        }}
                                                    >
                                                        <div className="card-body p-3 d-flex align-items-center justify-content-center gap-2">
                                                            <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', backgroundColor: `${THEME_COLOR}15` }}>
                                                                <Plus size={18} style={{ color: THEME_COLOR }} />
                                                            </div>
                                                            <span className="fw-bold" style={{ color: THEME_COLOR, fontSize: '0.9rem' }}>{t('Add New Item')}</span>
                                                        </div>
                                                    </motion.div>

                                                    {(!section.items || section.items.length === 0) && (
                                                        <div className="text-center py-4 border rounded-4 bg-white bg-opacity-50 border-dashed">
                                                            <p className="text-muted mb-0 x-small fw-medium opacity-50">{t('No items in this section')}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ))}

                        {/* Add New Section Card Button */}
                        <motion.div
                            whileHover={isMaxMenusReached ? {} : { scale: 1.01 }}
                            whileTap={isMaxMenusReached ? {} : { scale: 0.99 }}
                            onClick={addSection}
                            className="card border-2 border-dashed bg-white rounded-4 overflow-hidden shadow-sm transition-all cursor-pointer add-section-card"
                            style={{ borderColor: THEME_COLOR, opacity: isMaxMenusReached ? 0.5 : 1, pointerEvents: isMaxMenusReached ? 'none' : 'auto' }}
                        >
                            <div className="card-body p-4 d-flex align-items-center justify-content-center gap-3">
                                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: `${THEME_COLOR}20` }}>
                                    <Plus size={24} style={{ color: THEME_COLOR, strokeWidth: 2.5 }} />
                                </div>
                                <div className="text-center">
                                    <h6 className="mb-0 fw-bold" style={{ color: THEME_COLOR }}>
                                        {isMaxMenusReached ? t('Section Limit Reached') : t('Add New Section')}
                                    </h6>
                                    <p className="text-muted small mb-0" style={{ fontSize: '11px' }}>
                                        {isMaxMenusReached ? `${t('You reached your plan limit of')} ${menuLimit.limit} ${t('sections')}` : t('Create another category for your menu')}
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {sections.length === 0 && (
                            <div className="text-center py-5 border rounded-5 bg-white shadow-sm border-dashed">
                                <div className="bg-light rounded-circle p-3 d-inline-flex mb-3">
                                    <LayoutGrid size={32} className="text-muted opacity-25" />
                                </div>
                                <h6 className="fw-bold">{t('No sections found')}</h6>
                                <p className="text-muted small mx-auto" style={{ maxWidth: '250px' }}>{t('Start by creating your first category (e.g., Main Menu, Drinks, Starters)')}</p>
                                <button onClick={addSection} className="btn btn-sm text-white px-4 rounded-pill mt-2" style={{ backgroundColor: THEME_COLOR }}>
                                    <Plus size={14} className="me-1" /> {t('Create Section')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Right: Live Preview */}
            <div className="d-none d-xl-flex bg-white p-1 border-start shadow-lg" style={{ width: '450px', zIndex: 5, flexDirection: 'column', overflow: 'hidden' }}>
                <div className="d-flex align-items-center justify-content-between m-2 flex-shrink-0">
                    <div className="d-flex align-items-center gap-2">
                        <div className="bg-success rounded-circle" style={{ width: '8px', height: '8px' }}></div>
                        <h6 className="text-muted small text-uppercase fw-bold mb-0" style={{ letterSpacing: '1px' }}>{t('Live Menu Preview')}</h6>
                    </div>
                    <span className="badge bg-light text-muted fw-normal px-2 py-1" style={{ fontSize: '10px' }}>{t('REAL-TIME UPDATES')}</span>
                </div>
                <div className="flex-grow-1 d-flex align-items-center justify-content-center" style={{ overflow: 'hidden' }}>
                    <div className="border border-4 rounded-5 p-2 bg-dark shadow-lg position-relative" style={{ width: '320px', height: '100%', maxHeight: '640px', borderColor: '#333 !important' }}>
                        <div className="w-100 h-100 rounded-4 bg-white overflow-hidden shadow-inner">
                            <MobileView state={state} />
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .cursor-pointer { cursor: pointer; }
                .hover-shadow:hover { box-shadow: 0 .5rem 1rem rgba(0,0,0,.08)!important; }
                .hover-shadow-sm:hover { box-shadow: 0 .125rem .25rem rgba(0,0,0,.075)!important; }
                .hover-bg-silver:hover { background-color: #f8f9fa!important; }
                .hover-bg-light:hover { background-color: #f1f3f5!important; }
                .hover-text-primary:hover { color: ${THEME_COLOR}!important; }
                .hover-text-danger:hover { color: #dc3545!important; }
                .hover-border-silver:hover { border-color: #dee2e6!important; }
                .transition-all { transition: all 0.2s ease; }
                .transition-colors { transition: color 0.2s ease, background-color 0.2s ease; }
                .max-w-4xl { max-width: 56rem; }
                .ring-2 { box-shadow: 0 0 0 2px ${THEME_COLOR}; }
                .x-small { font-size: 0.7rem; }
                .btn-xs { padding: 0.1rem 0.4rem; font-size: 0.75rem; }
                .group-item:hover { transform: translateY(-1px); }
                .rotate-180 { transform: rotate(180deg); }
                .shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
                .border-dashed { border-style: dashed !important; }

                /* Pulsing border animation for add cards */
                @keyframes pulse-border {
                    0%, 100% {
                        border-color: ${THEME_COLOR};
                        box-shadow: 0 2px 8px rgba(0,0,0,0.1), 0 0 0 0 ${THEME_COLOR}40;
                    }
                    50% {
                        border-color: ${THEME_COLOR}dd;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.15), 0 0 0 6px ${THEME_COLOR}15;
                    }
                }

                .add-section-card, .add-item-card {
                    animation: pulse-border 2.5s ease-in-out infinite;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }

                .add-section-card:hover, .add-item-card:hover {
                    animation: none;
                    border-color: ${THEME_COLOR} !important;
                    box-shadow: 0 6px 16px ${THEME_COLOR}40 !important;
                    transform: translateY(-2px);
                }

                /* Custom scrollbar for better look */
                *::-webkit-scrollbar {
                  width: 5px;
                }
                *::-webkit-scrollbar-track {
                  background: transparent;
                }
                *::-webkit-scrollbar-thumb {
                  background: #ddd;
                  border-radius: 10px;
                }
                *::-webkit-scrollbar-thumb:hover {
                  background: #ccc;
                }
            `}</style>

            <AddItemDialog
                isOpen={isDialogOpen}
                onClose={() => {
                    setIsDialogOpen(false);
                    setEditingItem(null);
                }}
                onAdd={addItem}
                itemToEdit={editingItem}
                businessType={state.businessType || 'restaurant'}
                currency={state.siteInfo?.currency}
            />
        </div >
    );
};

export default MenuConfig;
